import React from "react";

import { css } from "@emotion/core";

import {
  Link,
  Text,
  useTheme,
  Typography,
  Colors as ThemeColors,
} from "@zapier/design-system";

import { Colors, breakpoints } from "@zapier/checkout/esm/constants";
import { formatCompanyName } from "./formatCompanyName";

type Props = {
  accountName?: string | null | undefined;
  companyName?: string | null | undefined;
  heading: string;
  /** Set this to true when the change plan page appears inside an inline upgrade nudge. */
  isEmbeddedInInlineUpgrade?: boolean;
  subheading?: string;
};

/* Delete after rebrand */
const accountNameCss = css`
  text-decoration: underline;
`;

const headerCss = css`
  margin-bottom: 4rem;
`;

const rebrandHeaderCss = css`
  margin-bottom: 1rem;

  @media screen and (max-width: ${breakpoints.mobileMax}px) {
    margin-bottom: 3rem;
  }
`;

const headingCss = css`
  color: ${Colors.deepSpace};
  font-size: 1.8em;
  font-weight: 600;
  margin-top: 1em;
  padding: 0;
  text-align: center;
`;

const rebrandHeadingCss = css`
  ${Typography.pageHeader7};
  color: ${ThemeColors.neutral800};
  margin-top: 0;

  @media screen and (max-width: ${breakpoints.mobileMax}px) {
    ${Typography.pageHeader9};
  }
`;

const subHeadingCss = css`
  color: ${Colors.steel};
  font-size: 1em;
  margin-top: 20px;
  text-align: center;
`;

const rebrandSubHeadingCss = css`
  ${Typography.paragraph3};
  color: ${ThemeColors.neutral800};
  margin-top: 0.5em;
`;

export const ChangePlanHeader = (props: Props) => {
  const { isRebrand } = useTheme();
  const formattedCompanyName = formatCompanyName(props.companyName);

  const standardContent = props.subheading && (
    <div css={[subHeadingCss, isRebrand && rebrandSubHeadingCss]}>
      {props.subheading}:{" "}
      <span css={!isRebrand && accountNameCss}>{props.accountName}</span>{" "}
      {formattedCompanyName}
    </div>
  );

  const inlineUpgradeHeaderContent = (
    <Text
      color={isRebrand ? "neutral700" : "gray60"}
      tag="p"
      textAlign="center"
      type={isRebrand ? "smallPrint1Medium" : "smallPrint1"}
    >
      You're upgrading the account{" "}
      <span style={{ textDecoration: "underline" }}>{props.accountName}</span>
      {formattedCompanyName ? " " + formattedCompanyName : null}. Have questions
      about what's included in your plan?{" "}
      <Link href="/app/billing/plans">Compare plans and pricing</Link>.
    </Text>
  );

  return (
    <header css={[headerCss, isRebrand && rebrandHeaderCss]}>
      <h1 css={[headingCss, isRebrand && rebrandHeadingCss]}>
        {props.heading}
      </h1>
      {props.isEmbeddedInInlineUpgrade
        ? inlineUpgradeHeaderContent
        : standardContent}
    </header>
  );
};
